import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ResourceForm } from "@/components/common/ResourceForm";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

export function useList(endpoint, params) {
  const [state, setState] = useState({ items: [], total: 0, loading: true });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/${endpoint}`, { params });
      if (Array.isArray(data)) setState({ items: data, total: data.length, loading: false });
      else setState({ items: data.items || [], total: data.total || 0, loading: false });
    } catch (e) {
      toast.error(apiError(e));
      setState({ items: [], total: 0, loading: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(params)]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

export default function ResourcePage({
  title, subtitle, endpoint, module, columns, fields, searchPlaceholder,
  emptyHint, buildPayload, defaultParams = {}, extraToolbar, extraHeader, paginated = true,
  formInitial, onRowClick, rowActionsExtra, bulk = true,
}) {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setDebounced(search); setPage(1); }, 350);
    return () => clearTimeout(timer.current);
  }, [search]);

  const params = paginated
    ? { page, page_size: 20, search: debounced, sort_by: sortBy, sort_dir: sortDir, ...defaultParams }
    : { search: debounced, ...defaultParams };
  const { items, total, loading, reload } = useList(endpoint, params);

  const onSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortBy(key); setSortDir("desc"); }
  };

  const save = async (values) => {
    setSaving(true);
    try {
      const payload = buildPayload ? buildPayload(values, editing) : values;
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? "Updated" : "Created");
      setFormOpen(false); setEditing(null); reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/${endpoint}/${delTarget.id}`);
      toast.success("Deleted");
      setDelTarget(null); reload();
    } catch (e) { toast.error(apiError(e)); setDelTarget(null); }
  };

  const bulkDelete = async () => {
    try {
      const { data } = await api.post(`/${endpoint}/bulk`, { action: "delete", ids: selected });
      toast.success(`Deleted ${data.affected} record(s)`);
      setSelected([]); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const canEdit = can(`${module}:edit`);
  const canDelete = can(`${module}:delete`);
  const canCreate = can(`${module}:create`);

  const rowActions = (canEdit || canDelete || rowActionsExtra)
    ? (r) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-menu-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {rowActionsExtra && rowActionsExtra(r, reload)}
          {canEdit && <DropdownMenuItem data-testid={`edit-${r.id}`} onClick={() => { setEditing(r); setFormOpen(true); }}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>}
          {canDelete && <DropdownMenuItem data-testid={`delete-${r.id}`} className="text-destructive" onClick={() => setDelTarget(r)}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  return (
    <div className="space-y-6">
      <PageHeader title={title} subtitle={subtitle}>
        {extraHeader}
        {canCreate && fields && (
          <Button data-testid={`add-${module}-btn`} onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="mr-1 h-4 w-4" /> New
          </Button>
        )}
      </PageHeader>

      <DataTable
        testId={`${module}-table`}
        columns={columns}
        rows={items}
        loading={loading}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={paginated ? setPage : undefined}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={paginated ? onSort : undefined}
        selectable={canDelete && bulk}
        selectedIds={selected}
        onSelectedChange={setSelected}
        onBulkDelete={canDelete && bulk ? bulkDelete : undefined}
        search={search}
        onSearch={setSearch}
        searchPlaceholder={searchPlaceholder}
        toolbar={extraToolbar}
        rowActions={rowActions}
        emptyHint={emptyHint}
      />

      {fields && (
        <ResourceForm
          open={formOpen}
          onOpenChange={setFormOpen}
          title={editing ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}
          fields={fields}
          initial={editing || formInitial}
          onSubmit={save}
          submitting={saving}
        />
      )}

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => !v && setDelTarget(null)}
        title="Delete this record?"
        description="This action cannot be undone and will be recorded in the audit log."
        confirmText="Delete"
        destructive
        onConfirm={remove}
      />
    </div>
  );
}
